import React from "react";
import {
  AlertMessage,
  Button,
  DropdownInput,
  FieldNames,
  FieldValidation,
  IAlertMessageProps,
  InputGroup,
  IOption,
  IPopupProps,
  Popup,
  required,
  requiredNonEmptyString,
  TextArea,
} from "component-library";

import { IApiErrorMessages } from "shared/models/api";

import InputManager, { Validators } from "services/InputManager";

import "./ReportConcernPopup.scss";
import { IReportConcernPopupModel } from "./ReportConcernPopup.model";

export interface IReportConcernPopupTexts {
  title: string;
  detailsLabel: string;
  detailsPlaceholder: string;
  typeDropdownLabel: string;
  typeDropdownPlaceholder: string;
  typeOptions: IOption<number>[];
  sendButton: string;
  sendButtonLoading: string;
  cancelButton: string;
  errors: IApiErrorMessages<IReportConcernPopupModel>;
}

export interface IReportConcernPopupDynamicProps {
  texts: IReportConcernPopupTexts;
  onSubmit(model: IReportConcernPopupModel): Promise<void>;
  hidePopup(): void;
}

export interface IReportConcernPopupProps
  extends IReportConcernPopupDynamicProps {}

export interface IReportConcernPopupState {
  model: IReportConcernPopupModel;
  validationData: FieldValidation<IReportConcernPopupModel>;
  submitLoading: boolean;
  isFormSubmitted: boolean;
}

export const INPUT_NAMES: FieldNames<IReportConcernPopupModel> = {
  typeId: "typeId",
  details: "details",
};

export class ReportConcernPopup extends React.PureComponent<
  IReportConcernPopupProps,
  IReportConcernPopupState
> {
  public readonly state: Readonly<IReportConcernPopupState> = {
    model: InputManager.generateModel<IReportConcernPopupModel>(INPUT_NAMES),
    validationData:
      InputManager.generateValidationDataConfig<IReportConcernPopupModel>(
        INPUT_NAMES
      ),
    submitLoading: false,
    isFormSubmitted: false,
  };

  //@ts-ignore
  private inputManager = new InputManager<IReportConcernPopupModel>(
    () => this.state.model,
    (model, callback) => this.setState({ model }, callback),
    (validationData) => this.setState({ validationData }),
    () => this.state.validationData,
    InputManager.generateInputsConfig<IReportConcernPopupModel>(
      INPUT_NAMES,
      this.state.model,
      this.createValidators()
    )
  );

  public render() {
    const { texts } = this.props;
    const popupProps: IPopupProps = {
      className: "c-report-concern-popup",
      width: { lg: 6, md: 7 },
      close: this.handleClose,
      priority: "high",
      texts: { closePopup: texts.cancelButton },
    };
    const alertProps: IAlertMessageProps = {
      texts: {
        title: texts.title,
      },
      buttons: [],
    };

    return (
      <Popup {...popupProps}>
        <AlertMessage {...alertProps} />
        <InputGroup
          inputConfigs={this.inputManager.inputs}
          triggerValidation={this.inputManager.validator}
          validationDone={this.inputManager.validate}
        >
          <DropdownInput<number>
            id="reportConcernDropdown"
            variant="select"
            options={texts.typeOptions}
            label={texts.typeDropdownLabel}
            placeholder={texts.typeDropdownPlaceholder}
            isFormSubmitted={this.state.isFormSubmitted}
            {...this.inputManager.getDropdownInputConfig<number>(
              INPUT_NAMES.typeId,
              texts.typeOptions,
              {
                isFormSubmitted: this.state.isFormSubmitted,
              }
            )}
          />
          <TextArea
            id="reportConcernText"
            maxLength={1000}
            label={texts.detailsLabel}
            placeholder={texts.detailsPlaceholder}
            isFormSubmitted={this.state.isFormSubmitted}
            {...this.inputManager.getInputConfig(INPUT_NAMES.details)}
          />
          <div className="c-report-concern-popup__control">
            <Button
              text={texts.sendButton}
              type="primary"
              onClick={this.handleSubmit}
              loading={this.state.submitLoading}
              loadingText={texts.sendButtonLoading}
            />
          </div>
          <div className="c-report-concern-popup__control">
            <Button
              text={texts.cancelButton}
              type="secondary"
              onClick={this.handleClose}
            />
          </div>
        </InputGroup>
      </Popup>
    );
  }

  public handleSubmit = () => {
    this.setState(
      {
        isFormSubmitted: true,
      },
      async () => {
        if (!this.inputManager.isValid()) {
          return;
        }

        this.setState({ submitLoading: true });
        await this.props.onSubmit(this.state.model);
      }
    );
  };

  public handleClose = () => {
    this.props.hidePopup();
  };

  private createValidators(): Validators<IReportConcernPopupModel> {
    const { texts } = this.props;

    return {
      [INPUT_NAMES.typeId]: [
        required(texts?.errors.fields.typeId.required ?? ""),
      ],
      [INPUT_NAMES.details]: [
        requiredNonEmptyString(texts?.errors.fields.details.required ?? ""),
      ],
    };
  }
}

export default ReportConcernPopup;
